import React, { useState, useMemo, useCallback } from 'react';
import CardCommon from '@op/components/CardCommon';
import useLanguage from '@op/hooks/useLanguage';
import classnames from 'classnames';
import './index.scss';
import { BrushSelectType, IRealtimeTrendAnalysis } from '../../../type';
import { lineIcon, tableIcon } from '@op/constants/chart';
import Table from './table';
import SwitchControls from './SwitchControls';
import LineChart from './lineChart';
import { useRealTimeTrendAnalysis } from '../../../hooks/useRealtimeTrendAnalysis';
import { downloadAction } from '@op/pages/download/utils';
import { formatStreamingTime } from '@op/pages/RealtimeStreaming/utils';
import { getSentimentIcon } from '@op/utils';
import SvgIcon from '@home/components/SvgIcon';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';

const RealtimeTrendAnalysis = (props: Omit<IRealtimeTrendAnalysis, "config">) => {
  const {
    title = 'Real-time Trend Analysis',
    className,
    onBrushSelect,
    filter,
    addRefreshTask,
    emptyCallback,
    isGST = false
  } = props;
  const { formatMessage } = useLanguage();
  const [chartType, setChartType] = useState('line');
  const [brushMode, setBrushMode] = useState<'latest5min' | 'adjustable'>(
    'adjustable',
  );
  const [selectedTimeRange, setSelectedTimeRange] = useState<{
    startTime: string;
    endTime: string;
  } | null>(null);

  const filterParams = useMemo(() => {
    if (!filter) return {};
    const { end_time, ...restFilter } = filter || {};
    return { ...restFilter };
  }, [filter]);

  const { data, loading } = useRealTimeTrendAnalysis({
    filter: filterParams,
    addRefreshTask,
    emptyCallback,
    isGST
  });

  // Handle brush selection callback with start/end time
  const handleBrushSelect = useCallback(
    (selectedData: BrushSelectType) => {
      if (onBrushSelect) {
        onBrushSelect(selectedData);
        buriedBtnPoint('AdjustTimePeriod', selectedData);
      }
      setSelectedTimeRange(selectedData.liveTime);
    },
    [onBrushSelect],
  );

  // Handle brush mode change
  const handleBrushModeChange = useCallback(
    (mode: 'latest5min' | 'adjustable') => {
      setBrushMode(mode);
      buriedBtnPoint('ChangeTimePeriodType', { mode });
    },
    [],
  );

  return (
    <CardCommon
      title={formatMessage({ id: title })}
      className={classnames('dbo-realtime-trend-analysis', className)}
      operationInfo={[chartType, setChartType]}
      operations={[tableIcon, lineIcon]}
      hasDownload={data?.timeline?.length > 0}
      subFilterBeforeElement={
        <>
          {chartType === 'line' && (
            <SwitchControls
              brushMode={brushMode}
              onBrushModeChange={handleBrushModeChange}
              selectedTimeRange={selectedTimeRange}
            />
          )}
        </>
      }
      loading={loading}
      loadingHeight={350}
      handleDownloadOnClick={() => {
        downloadAction({
          key: 'RealtimeStreamingTrendAnalysis',
          fileName: `Real-time Trend Analysis`,
          filter: {},
          auxiliaryParams: {},
          data: data?.timeline?.map((item: { topics: any[] }) => ({
            ...item,
            topics: item?.topics?.length ? item?.topics?.join(', ') : '-',
          })),
        });
      }}
      empty={data?.timeline?.length === 0}
      emptyHeight={350}
    >
      <>
        {chartType === 'line' && (
          <LineChart
            data={data}
            onBrushSelect={handleBrushSelect}
            brushMode={brushMode}
            selectedTimeRange={selectedTimeRange}
            filter={filter}
          />
        )}
        {chartType === 'table' && (
          <Table
            maxHeight={330}
            data={data?.timeline || []}
            columns={[
              {
                name: 'Playback Time',
                value: 'playback_time',
                formatValue: (val: string) => formatStreamingTime(val),
              },
              {
                name: 'Number of Live Chats',
                value: 'mentions',
              },
              {
                name: 'Live Chats Sentiment',
                value: 'avg_sentiment',
                formatValue: (value: number) => {
                  if (!value) return '-';
                  return <SvgIcon type={getSentimentIcon(value)} />;
                },
              },
              // {
              //   name: 'Streamer Sentiment',
              //   value: 'topics_avg_sentiment',
              //   formatValue: (value: number) => {
              //     if (!value) return '-';
              //     return <SvgIcon type={getSentimentIcon(value)} />;
              //   },
              // },
              {
                name: `Streamer's Ongoing Topic`,
                value: 'topics',
                formatValue: (val: string[]) => (val ? val?.join(', ') : '-'),
              },
            ]}
          />
        )}
      </>
    </CardCommon>
  );
};

export default RealtimeTrendAnalysis;
